import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TaskDrawerService } from '../../../pages/tasks/services/task-drawer.service';
import { TicketsDrawerService } from '../../../pages/tickets/services/tickets-drawer.service';
import { MeetingDrawerService } from '../../../pages/meeting/services/meeting-drawer.service';
import { CallDialerService } from '../../../pages/call/services/call-dialer.service';
import { canUseCallDialer } from '../../../pages/call/call.constants';
import { EmailComposeService } from '../../../pages/correos/services/email-compose.service';
import { WhatsappComposeService } from '../../../pages/whatsapp/services/whatsapp-compose.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../../pages/auth-pages/services/auth.service';

/**
 * Botón "Acciones rápidas" estilo HubSpot. Reutilizable desde cualquier vista
 * (detalle de contacto/empresa/lead/deal o el header global). Despliega un menú
 * con: Enviar correo, Crear tarea, Crear ticket.
 *
 * Uso:
 *   <app-quick-actions-menu
 *     [contactId]="contact.id"
 *     [contactName]="contact.fullname"
 *     [contactEmail]="contact.mail">
 *   </app-quick-actions-menu>
 */
@Component({
  selector: 'app-quick-actions-menu',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './quick-actions-menu.component.html',
})
export class QuickActionsMenuComponent {
  @Input() label = 'Acciones';
  @Input() variant: 'button' | 'icon' = 'button';
  @Input() align: 'left' | 'right' = 'right';

  @Input() contactId?: number;
  @Input() contactName?: string;
  @Input() contactEmail?: string;
  @Input() contactPhone?: string;
  @Input() companyId?: number;
  @Input() companyName?: string;
  @Input() dealId?: number;
  @Input() dealName?: string;
  @Input() leadId?: number;
  @Input() leadName?: string;

  /** Se emite después de disparar cualquier acción (enviar correo, crear tarea
   *  o crear ticket). Útil para que el componente padre (ej. un detail-modal)
   *  cierre su propia vista al abrir el drawer correspondiente. */
  @Output() actionTriggered = new EventEmitter<
    'email' | 'whatsapp' | 'task' | 'ticket' | 'meeting' | 'call'
  >();

  isOpen = false;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly tasks = inject(TaskDrawerService);
  private readonly tickets = inject(TicketsDrawerService);
  private readonly meetings = inject(MeetingDrawerService);
  private readonly callDialer = inject(CallDialerService);
  private readonly emailCompose = inject(EmailComposeService);
  private readonly whatsappCompose = inject(WhatsappComposeService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);

  /** El marcador (y por ende "Registrar llamada") solo está disponible para
   *  roles Sales Rep o superiores; el widget no se monta para el resto. */
  readonly canRegisterCall = canUseCallDialer(this.auth.getCurrentUser()?.roleName);

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc(): void {
    if (this.isOpen) this.close();
  }

  sendEmail(): void {
    // Inc 24: propaga el contexto de origen (LEAD/DEAL/…) para que el correo
    // enviado se registre en el timeline de esa entidad y aparezca en enviados.
    const ctxType = this.contextEntityType();
    const ctxId = this.contextEntityId();
    // Sin correo no podemos precargar destinatario, pero igual abrimos el
    // compose vacío para que el usuario lo escriba/busque a mano.
    if (this.contactEmail) {
      this.emailCompose.open({
        email: this.contactEmail,
        fullName: this.contactName,
        contactId: this.contactId,
        contextEntityType: ctxType,
        contextEntityId: ctxId,
      });
    } else {
      this.toast.info({ title: 'Sin correo', message: 'Este contacto no tiene correo; complétalo en el compose.' });
      this.emailCompose.open({
        contactId: this.contactId,
        contextEntityType: ctxType,
        contextEntityId: ctxId,
      });
    }
    this.close();
    this.actionTriggered.emit('email');
  }

  sendWhatsapp(): void {
    // Sin telefono no se puede precargar, pero la ventana se abre igual para
    // escribirlo a mano: mismo criterio que sendEmail() con el correo vacio.
    if (!this.contactPhone) {
      this.toast.info({
        title: 'Sin telefono',
        message: 'Este contacto no tiene telefono; escribelo en la ventana.',
      });
    }
    this.whatsappCompose.open({
      phone: this.contactPhone,
      fullName: this.contactName,
      contactId: this.contactId,
    });
    this.close();
    this.actionTriggered.emit('whatsapp');
  }

  createTask(): void {
    this.tasks.open({
      contactId: this.contactId,
      contactName: this.contactName,
      companyId: this.companyId,
      companyName: this.companyName,
      dealId: this.dealId,
      dealName: this.dealName,
      leadId: this.leadId,
      leadName: this.leadName,
    });
    this.close();
    this.actionTriggered.emit('task');
  }

  createTicket(): void {
    this.tickets.open({
      contactId: this.contactId,
      contactName: this.contactName,
      companyId: this.companyId,
      companyName: this.companyName,
      dealId: this.dealId,
      leadId: this.leadId,          // Inc 25: registra el lead de origen
      leadName: this.leadName,
    });
    this.close();
    this.actionTriggered.emit('ticket');
  }

  createMeeting(): void {
    this.meetings.open({
      contactId: this.contactId,
      contactName: this.contactName,
      companyId: this.companyId,
      companyName: this.companyName,
      dealId: this.dealId,
      dealName: this.dealName,
    });
    this.close();
    this.actionTriggered.emit('meeting');
  }

  registerCall(): void {
    // No existe registro manual de llamada: abrimos el marcador de Twilio con el
    // teléfono del contacto precargado (si lo hay). La llamada queda registrada
    // en el historial al completarse vía Twilio.
    this.callDialer.open(this.contactPhone ?? '');
    this.close();
    this.actionTriggered.emit('call');
  }

  private contextEntityType(): string | undefined {
    // Inc 24: el backend espera el enum en MAYÚSCULAS (contextEntityType:"LEAD").
    if (this.dealId) return 'DEAL';
    if (this.leadId) return 'LEAD';
    if (this.companyId) return 'COMPANY';
    if (this.contactId) return 'CONTACT';
    return undefined;
  }

  private contextEntityId(): number | undefined {
    return this.dealId ?? this.leadId ?? this.companyId ?? this.contactId;
  }
}
